import { TFunction } from 'i18next';
import { Node } from 'reactflow';
import { NodeConfiguration } from 'src/redux/workflows/types';
import { formatActionTypeKey } from './general';

export interface WorkflowIssue {
  nodeId: string | null;
  message: string;
}

const BUILDER_NODE_TYPES = ['emailBuilder', 'notificationBuilder', 'caseBuilder'];

export const validateWorkflow = (
  nodes: Node[],
  nodeConfigurations: Record<string, NodeConfiguration>,
  hasTrigger: boolean,
  t: TFunction,
): WorkflowIssue[] => {
  const issues: WorkflowIssue[] = [];
  const triggerNode = nodes.find(node => node.type === 'trigger');
  if (!hasTrigger) {
    issues.push({ nodeId: triggerNode?.id ?? null, message: t('workflows.validation.no_trigger') });
  }

  const steps = nodes.filter(node => node.type !== 'plus' && node.type !== 'trigger');
  if (!steps.some(node => nodeConfigurations[node.id]?.nodeType === 'action')) {
    issues.push({ nodeId: null, message: t('workflows.validation.no_action') });
  }

  steps.forEach(node => {
    const config = nodeConfigurations[node.id];
    if (!config) {
      issues.push({ nodeId: node.id, message: t('workflows.validation.node_not_configured') });
      return;
    }
    if (node.type === 'condition') {
      const conditions = (config.data as any).conditions || [];
      if (conditions.length === 0 || conditions.some((c: any) => !c.attribute || !c.condition)) {
        issues.push({ nodeId: node.id, message: t('workflows.validation.condition_incomplete') });
      }
      return;
    }
    if (config.nodeType !== 'action') return;
    const action = config.data as any;
    const isProduct = node.type === 'productBuilder' || formatActionTypeKey(action.actionType || '') === 'product';
    if (isProduct) {
      if (action.selectedProductId === undefined || action.selectedProductId === null) {
        issues.push({ nodeId: node.id, message: t('workflows.validation.product_missing') });
      }
      if (node.type === 'productBuilder' && !action.unit) {
        issues.push({ nodeId: node.id, message: t('workflows.validation.unit_missing') });
      }
      if (!action.amount || action.amount < 1) {
        issues.push({ nodeId: node.id, message: t('workflows.validation.amount_invalid') });
      }
      return;
    }
    if (!BUILDER_NODE_TYPES.includes(node.type || '') && !action.title && !action.returnValue) {
      issues.push({ nodeId: node.id, message: t('workflows.validation.action_incomplete') });
    }
  });

  return issues;
};
